import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { withStyles } from 'vopay';
import { BodyText, FilterChips, SearchBar, Skeleton, Title } from 'UI';
import IntegrationCard from './IntegrationCard';
import IntegrationSetupDrawer from './SetupDrawer/IntegrationSetupDrawer';
import { INTEGRATION_GROUPS } from './integrationsCatalogue';
import useIntegrationCatalogue from './useIntegrationCatalogue';
import useDisconnectRequiredDialog from './useDisconnectRequiredDialog';
import useIntegrationGroupLabels from './useIntegrationGroupLabels';
import useIntegrationUpgradeDialog from './useIntegrationUpgradeDialog';
import { IntegrationCardData, IntegrationGroupFilter } from './types';
import styles from './IntegrationsPage.module.scss';

const cn = withStyles(styles);

const GROUP_FILTERS: IntegrationGroupFilter[] = ['all', ...INTEGRATION_GROUPS];

// Placeholder cards shown until the account's country and integration states have arrived
const SKELETON_CARDS = [0, 1, 2, 3, 4, 5];

export default function IntegrationsPage() {
  const history = useHistory();
  const openUpgradeDialog = useIntegrationUpgradeDialog();
  const openDisconnectRequiredDialog = useDisconnectRequiredDialog();
  const { t } = useTranslation('translations', { keyPrefix: 'Integrations.IntegrationsPage' });
  const { t: tIntegrations } = useTranslation('translations', { keyPrefix: 'Integrations' });
  const groupLabels = useIntegrationGroupLabels();
  const { integrations, connectedRailzServices, isLoading } = useIntegrationCatalogue();
  const [group, setGroup] = useState<IntegrationGroupFilter>('all');
  // Filtering is client-side over a dozen static cards, so it runs on every keystroke — no debounce
  const [searchTerm, setSearchTerm] = useState('');
  // The integration whose credentials the drawer is collecting. It is kept after the drawer
  // closes so the close animation still has content to render.
  const [setupIntegration, setSetupIntegration] = useState<IntegrationCardData | null>(null);
  const [isSetupOpen, setIsSetupOpen] = useState(false);

  const searchedTerm = searchTerm.trim().toLowerCase();

  const visibleIntegrations = integrations.filter(
    (integration) =>
      (group === 'all' || integration.group === group) && integration.name.toLowerCase().includes(searchedTerm),
  );

  function handleActionClick(integration: IntegrationCardData) {
    if (integration.state === 'locked') {
      void openUpgradeDialog(integration);

      return;
    }

    // Credentials are set (and later replaced) in the drawer, over the catalogue
    if (integration.setup) {
      setSetupIntegration(integration);
      setIsSetupOpen(true);

      return;
    }

    // Only one accounting software can be connected at a time, so a second ERP card can't start a
    // connection while another is live — support has to disconnect the first one
    const [connectedService] = connectedRailzServices;

    if (integration.group === 'accountingAndErp' && integration.state === 'available' && connectedService) {
      void openDisconnectRequiredDialog(integration, connectedService);

      return;
    }

    // Plaid (until ID-13265) and the ERP cards still configure on their own page
    if (integration.route) history.push(integration.route);
  }

  function renderGrid() {
    if (isLoading) {
      return (
        <ul className={cn('grid')} data-testid="integrations-grid-skeleton">
          {SKELETON_CARDS.map((index) => (
            <li key={index}>
              <Skeleton height={260} borderRadius={12} />
            </li>
          ))}
        </ul>
      );
    }

    if (visibleIntegrations.length === 0) {
      return (
        <BodyText className={cn('emptyState')}>
          {t('noResults', { defaultValue: 'No integrations match your search' })}
        </BodyText>
      );
    }

    return (
      <ul className={cn('grid')} data-testid="integrations-grid">
        {visibleIntegrations.map((integration) => (
          <li key={integration.id}>
            <IntegrationCard integration={integration} onActionClick={() => handleActionClick(integration)} />
          </li>
        ))}
      </ul>
    );
  }

  return (
    <div className={cn('page')}>
      <header className={cn('header')}>
        <Title level="1">{tIntegrations('pageTitle', { defaultValue: 'Integrations' })}</Title>

        <BodyText className={cn('subTitle')}>
          {tIntegrations('pageSubTitle', {
            defaultValue: 'Connect with your favorite solutions and manage everything in one place',
          })}
        </BodyText>
      </header>

      <div className={cn('toolbar')}>
        <FilterChips
          options={GROUP_FILTERS.map((filter) => ({ value: filter, label: groupLabels[filter] }))}
          value={group}
          onChange={setGroup}
          aria-label={t('groupFilterLabel', { defaultValue: 'Filter integrations by category' })}
        />

        <SearchBar
          className={cn('search')}
          name="integrations-search"
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder={t('searchPlaceholder', { defaultValue: 'Search integrations...' })}
        />
      </div>

      {/* The heading names whatever the chips have narrowed the grid to (ID-13622) */}
      <Title level="2" className={cn('sectionTitle')}>
        {group === 'all' ? t('allIntegrations', { defaultValue: 'All Integrations' }) : groupLabels[group]}
      </Title>

      {renderGrid()}

      {setupIntegration?.setup && (
        <IntegrationSetupDrawer
          // Remount per integration and state: a half-filled Flinks form never bleeds into Inverite's, and a
          // card that has just been connected reopens locked instead of on its previous step
          key={`${setupIntegration.id}:${setupIntegration.state}`}
          integration={setupIntegration}
          setup={setupIntegration.setup}
          isOpen={isSetupOpen}
          onClose={() => setIsSetupOpen(false)}
        />
      )}
    </div>
  );
}
